import {
	ArrowLeft,
	Building2,
	Calendar,
	Clock,
	Copy,
	DollarSign,
	Edit2,
	ExternalLink,
	History,
	Mail,
	Trash2,
	User,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Badge,
	Button,
	CopyButton,
	Modal,
	Notification,
	Timeline,
	useNotification,
} from "../../components/FormComponents";
import StatusBadge from "../../components/StatusBadge";
import { useProposalDetail } from "../../hooks/useProposalDetail";
import {
	formatCurrency,
	formatDate,
	formatDateShort,
} from "../../utils/format";

export default function ProposalDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { notification, showNotification } = useNotification();
	const {
		proposal,
		items,
		proposalLink,
		tokenExpiresAt,
		proposalResponse,
		loading,
		sendingEmail,
		cloning,
		deleting,
		versions,
		versionModalOpen,
		setVersionModalOpen,
		selectedVersion,
		setSelectedVersion,
		loadingVersion,
		handleSendEmail,
		handleClone,
		handleDelete,
		handleViewVersion,
		expirationInfo,
	} = useProposalDetail(id, showNotification);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-12">
				<p className="text-slate-300">Carregando...</p>
			</div>
		);
	}

	if (!proposal) {
		return (
			<div className="text-center py-12">
				<p className="text-slate-400">Proposta não encontrada</p>
				<Button
					type="button"
					onClick={() => navigate("/admin/proposals")}
					variant="secondary"
					className="mt-4"
				>
					Voltar
				</Button>
			</div>
		);
	}

	const isPending = proposal.status === "PENDING";

	const timelineItems = [
		{
			label: "Proposta criada",
			date: formatDate(proposal.created_at),
			completed: true,
		},
		{
			label:
				proposal.status === "ACCEPTED"
					? "Proposta aceita"
					: proposal.status === "REJECTED"
						? "Proposta rejeitada"
						: "Aguardando resposta",
			date: proposalResponse?.response_type
				? formatDate(
						proposalResponse.responded_at || proposalResponse.created_at,
					)
				: null,
			completed: proposal.status !== "PENDING",
			active: proposal.status === "PENDING",
		},
	];

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			{/* Header */}
			<div className="flex items-center justify-between flex-wrap gap-4">
				<div className="flex items-center gap-4">
					<button
						type="button"
						onClick={() => navigate("/admin/proposals")}
						className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition"
						aria-label="Voltar para a lista de propostas"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<div>
						<h1 className="text-2xl font-bold text-white flex items-center gap-3">
							<span className="font-mono text-cyan-400">
								{proposal.proposal_number}
							</span>
							<StatusBadge status={proposal.status} />
						</h1>
						{proposal.title && (
							<p className="text-slate-400 mt-1">{proposal.title}</p>
						)}
					</div>
				</div>
				<div className="flex gap-2">
					{proposalLink && (
						<CopyButton text={proposalLink} label="Copiar Link" />
					)}
					{isPending && (
						<Button
							type="button"
							variant="secondary"
							onClick={() => navigate(`/admin/proposals/new?edit=${id}`)}
						>
							<Edit2 className="w-4 h-4 inline mr-1" />
							Editar
						</Button>
					)}
					<Button
						type="button"
						onClick={handleClone}
						loading={cloning}
						variant="secondary"
					>
						<Copy className="w-4 h-4 inline mr-1" />
						Clonar
					</Button>
					<Button
						type="button"
						onClick={handleSendEmail}
						loading={sendingEmail}
						variant="secondary"
					>
						<Mail className="w-4 h-4 inline mr-1" />
						Enviar Email
					</Button>
					{isPending && (
						<Button
							type="button"
							onClick={handleDelete}
							loading={deleting}
							variant="danger"
						>
							<Trash2 className="w-4 h-4 inline mr-1" />
							Excluir
						</Button>
					)}
					{proposalLink && (
						<Button
							type="button"
							variant="ghost"
							onClick={() => window.open(proposalLink, "_blank")}
							aria-label="Abrir proposta em nova aba"
						>
							<ExternalLink className="w-4 h-4" />
						</Button>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Main Info */}
				<div className="lg:col-span-2 space-y-6">
					{/* Proposal Info */}
					<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
						<h2 className="text-lg font-semibold text-white mb-4">
							Informações da Proposta
						</h2>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-1">
								<p className="text-xs text-slate-500">Cliente</p>
								<p className="text-sm text-white font-medium flex items-center gap-2">
									<User className="w-4 h-4 text-slate-400" />
									{proposal.client_name}
								</p>
							</div>
							<div className="space-y-1">
								<p className="text-xs text-slate-500">Empresa</p>
								<p className="text-sm text-white font-medium flex items-center gap-2">
									<Building2 className="w-4 h-4 text-slate-400" />
									{proposal.company_name}
								</p>
							</div>
							<div className="space-y-1">
								<p className="text-xs text-slate-500">Data de Criação</p>
								<p className="text-sm text-slate-300 flex items-center gap-2">
									<Calendar className="w-4 h-4 text-slate-400" />
									{formatDate(proposal.created_at)}
								</p>
							</div>
							<div className="space-y-1">
								<p className="text-xs text-slate-500">Valor Total</p>
								<p className="text-sm text-green-400 font-bold flex items-center gap-2">
									<DollarSign className="w-4 h-4" />
									{formatCurrency(proposal.total_value)}
								</p>
							</div>
						</div>
						{proposal.description && (
							<div className="mt-4 pt-4 border-t border-slate-700">
								<p className="text-xs text-slate-500 mb-1">Descrição</p>
								<p className="text-sm text-slate-300">{proposal.description}</p>
							</div>
						)}
						{proposal.notes && (
							<div className="mt-4 pt-4 border-t border-slate-700">
								<p className="text-xs text-slate-500 mb-1">Notas</p>
								<p className="text-sm text-slate-300">{proposal.notes}</p>
							</div>
						)}
					</div>

					{/* Items */}
					<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
						<h2 className="text-lg font-semibold text-white mb-4">
							Itens da Proposta
						</h2>
						{items.length > 0 ? (
							<div className="space-y-3">
								{items.map((item, idx) => (
									<div
										key={item.id || `prop-item-${idx}`}
										className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-700/50"
									>
										<div className="flex items-center gap-3">
											<div
												className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
													item.item_type === "plan"
														? "bg-cyan-600/20 text-cyan-400"
														: "bg-purple-600/20 text-purple-400"
												}`}
											>
												{item.item_type === "plan" ? "P" : "M"}
											</div>
											<div>
												<p className="text-sm font-medium text-white">
													{item.name || `Item #${item.item_id}`}
												</p>
												<p className="text-xs text-slate-500">
													{item.item_type === "plan" ? "Plano" : "Módulo"} -
													Qtd: {item.quantity}
												</p>
											</div>
										</div>
										<span className="text-green-400 font-bold">
											{formatCurrency(parseFloat(item.value) * item.quantity)}
										</span>
									</div>
								))}
								<div className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-600/20 to-cyan-500/10 border border-cyan-500/30 rounded-xl mt-4">
									<span className="text-sm font-semibold text-white">
										Total
									</span>
									<span className="text-xl font-bold text-cyan-400">
										{formatCurrency(proposal.total_value)}
									</span>
								</div>
							</div>
						) : (
							<p className="text-slate-500 text-sm">Nenhum item encontrado</p>
						)}
					</div>

					{/* Response Details */}
					{proposalResponse?.response_type && (
						<div
							className={`backdrop-blur border rounded-xl p-6 ${
								proposalResponse.response_type === "accepted"
									? "bg-green-900/20 border-green-500/20"
									: "bg-red-900/20 border-red-500/20"
							}`}
						>
							<h2 className="text-lg font-semibold text-white mb-4">
								{proposalResponse.response_type === "accepted"
									? "Detalhes da Aprovação"
									: "Detalhes da Rejeição"}
							</h2>
							<div className="grid grid-cols-2 gap-4">
								{proposalResponse.approver_name && (
									<div>
										<p className="text-xs text-slate-400">Responsável</p>
										<p className="text-sm text-white font-medium">
											{proposalResponse.approver_name}
										</p>
									</div>
								)}
								{proposalResponse.approver_email && (
									<div>
										<p className="text-xs text-slate-400">Email</p>
										<p className="text-sm text-slate-300">
											{proposalResponse.approver_email}
										</p>
									</div>
								)}
								{proposalResponse.approver_role && (
									<div>
										<p className="text-xs text-slate-400">Cargo</p>
										<p className="text-sm text-slate-300">
											{proposalResponse.approver_role}
										</p>
									</div>
								)}
								{(proposalResponse.responded_at ||
									proposalResponse.created_at) && (
									<div>
										<p className="text-xs text-slate-400">Data da Resposta</p>
										<p className="text-sm text-slate-300">
											{formatDate(
												proposalResponse.responded_at ||
													proposalResponse.created_at,
											)}
										</p>
									</div>
								)}
							</div>
							{proposalResponse.rejection_reason && (
								<div className="mt-4 pt-4 border-t border-slate-700/50">
									<p className="text-xs text-slate-400">Motivo da Rejeição</p>
									<p className="text-sm text-red-300 font-medium mt-1">
										{proposalResponse.rejection_reason}
									</p>
								</div>
							)}
							{proposalResponse.observations && (
								<div className="mt-3">
									<p className="text-xs text-slate-400">Observações</p>
									<p className="text-sm text-slate-300 mt-1">
										{proposalResponse.observations}
									</p>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Sidebar */}
				<div className="space-y-6">
					{/* Timeline */}
					<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
						<h3 className="text-sm font-semibold text-white mb-4">
							Status da Proposta
						</h3>
						<Timeline items={timelineItems} />
					</div>

					{/* Expiration */}
					{expirationInfo && (
						<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
							<h3 className="text-sm font-semibold text-white mb-3">
								Expiração
							</h3>
							<div className="flex items-center gap-3">
								<Clock className="w-4 h-4 text-slate-400" />
								<Badge variant={expirationInfo.variant}>
									{expirationInfo.label}
								</Badge>
							</div>
							{tokenExpiresAt && (
								<p className="text-xs text-slate-500 mt-2">
									{expirationInfo.days > 0
										? `Válida até ${formatDateShort(tokenExpiresAt)}`
										: `Expirou em ${formatDateShort(tokenExpiresAt)}`}
								</p>
							)}
						</div>
					)}

					{/* Client Link */}
					{proposalLink && (
						<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
							<h3 className="text-sm font-semibold text-white mb-3">
								Link do Cliente
							</h3>
							<div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 break-all">
								<code className="text-xs text-cyan-300 font-mono">
									{proposalLink}
								</code>
							</div>
							<div className="flex gap-2 mt-3">
								<CopyButton text={proposalLink} label="Copiar" />
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => window.open(proposalLink, "_blank")}
								>
									<ExternalLink className="w-3 h-3" /> Abrir
								</Button>
							</div>
						</div>
					)}

					{/* Version History */}
					<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
						<h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
							<History className="w-4 h-4 text-slate-400" />
							Histórico de Versões
						</h3>
						{versions.length > 0 ? (
							<div className="space-y-2">
								{versions.map((version, idx) => (
									<button
										type="button"
										key={version.id || `version-${idx}`}
										onClick={() => handleViewVersion(version.id)}
										className="w-full text-left p-3 bg-slate-800/30 hover:bg-slate-800/60 rounded-lg border border-slate-700/50 hover:border-slate-600 transition-all"
									>
										<div className="flex items-center justify-between">
											<span className="text-sm font-medium text-white">
												Versão {version.version_number}
											</span>
											<span className="text-xs text-slate-500">
												{formatDateShort(version.created_at)}
											</span>
										</div>
										{version.changed_by_name && (
											<p className="text-xs text-slate-400 mt-1">
												por {version.changed_by_name}
											</p>
										)}
									</button>
								))}
							</div>
						) : (
							<p className="text-xs text-slate-500">
								Nenhuma versão registrada
							</p>
						)}
					</div>

					{/* Actions */}
					<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
						<h3 className="text-sm font-semibold text-white mb-3">Ações</h3>
						<div className="space-y-2">
							<Button
								type="button"
								onClick={handleSendEmail}
								loading={sendingEmail}
								variant="secondary"
								className="w-full"
							>
								<Mail className="w-4 h-4 inline mr-2" />
								Enviar por Email
							</Button>
							<Button
								type="button"
								variant="ghost"
								className="w-full"
								onClick={() => navigate(`/admin/clients/${proposal.client_id}`)}
							>
								<User className="w-4 h-4 inline mr-2" />
								Ver Cliente
							</Button>
						</div>
					</div>
				</div>
			</div>

			{/* Version Detail Modal */}
			<Modal
				isOpen={versionModalOpen}
				title={
					selectedVersion
						? `Versão ${selectedVersion.version_number}`
						: "Detalhes da Versão"
				}
				onClose={() => {
					setVersionModalOpen(false);
					setSelectedVersion(null);
				}}
				size="lg"
			>
				{loadingVersion ? (
					<div className="flex items-center justify-center py-8">
						<p className="text-slate-300">Carregando...</p>
					</div>
				) : selectedVersion ? (
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<p className="text-xs text-slate-500">Versão</p>
								<p className="text-sm text-white font-medium">
									{selectedVersion.version_number}
								</p>
							</div>
							<div>
								<p className="text-xs text-slate-500">Data</p>
								<p className="text-sm text-slate-300">
									{formatDate(selectedVersion.created_at)}
								</p>
							</div>
							{selectedVersion.changed_by_name && (
								<div>
									<p className="text-xs text-slate-500">Alterado por</p>
									<p className="text-sm text-slate-300">
										{selectedVersion.changed_by_name}
									</p>
								</div>
							)}
						</div>

						{selectedVersion.data && (
							<div className="mt-4 pt-4 border-t border-slate-700">
								<h4 className="text-sm font-semibold text-white mb-3">
									Dados da Proposta
								</h4>
								<div className="grid grid-cols-2 gap-3">
									{selectedVersion.data.title && (
										<div>
											<p className="text-xs text-slate-500">Título</p>
											<p className="text-sm text-slate-300">
												{selectedVersion.data.title}
											</p>
										</div>
									)}
									{selectedVersion.data.client_name && (
										<div>
											<p className="text-xs text-slate-500">Cliente</p>
											<p className="text-sm text-slate-300">
												{selectedVersion.data.client_name}
											</p>
										</div>
									)}
									{selectedVersion.data.total_value && (
										<div>
											<p className="text-xs text-slate-500">Valor Total</p>
											<p className="text-sm text-green-400 font-bold">
												{formatCurrency(selectedVersion.data.total_value)}
											</p>
										</div>
									)}
									{selectedVersion.data.status && (
										<div>
											<p className="text-xs text-slate-500">Status</p>
											<StatusBadge status={selectedVersion.data.status} />
										</div>
									)}
								</div>
								{selectedVersion.data.description && (
									<div className="mt-3">
										<p className="text-xs text-slate-500">Descrição</p>
										<p className="text-sm text-slate-300 mt-1">
											{selectedVersion.data.description}
										</p>
									</div>
								)}
								{selectedVersion.data.notes && (
									<div className="mt-3">
										<p className="text-xs text-slate-500">Notas</p>
										<p className="text-sm text-slate-300 mt-1">
											{selectedVersion.data.notes}
										</p>
									</div>
								)}
							</div>
						)}

						{selectedVersion.items &&
							Array.isArray(selectedVersion.items) &&
							selectedVersion.items.length > 0 && (
								<div className="mt-4 pt-4 border-t border-slate-700">
									<h4 className="text-sm font-semibold text-white mb-3">
										Itens
									</h4>
									<div className="space-y-2">
										{selectedVersion.items.map((item, idx) => (
											<div
												key={item.id || `v-item-${idx}`}
												className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-slate-700/50"
											>
												<div>
													<p className="text-sm font-medium text-white">
														{item.name || `Item #${item.item_id}`}
													</p>
													<p className="text-xs text-slate-500">
														{item.item_type === "plan" ? "Plano" : "Módulo"} -
														Qtd: {item.quantity}
													</p>
												</div>
												<span className="text-green-400 font-bold text-sm">
													{formatCurrency(
														parseFloat(item.value) * item.quantity,
													)}
												</span>
											</div>
										))}
									</div>
								</div>
							)}
					</div>
				) : null}
			</Modal>
		</div>
	);
}
